import React, { useEffect, useState } from "react";
import { getJobs, addJob, updateJob, deleteJob } from "../services/jobsService";
import { isSupabaseConfigured } from "../supabase/client";

const TYPES = ["Full-time", "Part-time", "Contract", "Internship"];

const emptyForm = {
  title: "",
  department: "",
  location: "",
  type: "Full-time",
  description: "",
  applyEmail: "",
  active: true,
};

const JobsAdmin = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setJobs(await getJobs());
    } catch (e) {
      setError("Couldn't load jobs. Check Supabase config / rules.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isSupabaseConfigured) load();
    else setLoading(false);
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setModalOpen(true);
  };

  const openEdit = (j) => {
    setEditingId(j.id);
    setForm({
      title: j.title || "",
      department: j.department || "",
      location: j.location || "",
      type: j.type || "Full-time",
      description: j.description || "",
      applyEmail: j.applyEmail || "",
      active: j.active !== false,
    });
    setError("");
    setModalOpen(true);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        department: form.department.trim(),
        location: form.location.trim(),
        type: form.type,
        description: form.description.trim(),
        applyEmail: form.applyEmail.trim(),
        active: form.active,
      };
      if (editingId) await updateJob(editingId, payload);
      else await addJob(payload);
      setModalOpen(false);
      await load();
    } catch (err) {
      setError(err.message || "Something went wrong while saving.");
    }
    setSaving(false);
  };

  const toggleActive = async (j) => {
    await updateJob(j.id, { active: !(j.active !== false) });
    await load();
  };

  const handleDelete = async (j) => {
    if (!window.confirm(`Delete “${j.title}”? This can't be undone.`)) return;
    await deleteJob(j.id);
    await load();
  };

  return (
    <>
      <div className="admin-topbar">
        <h2>Job Openings</h2>
        <button className="btn btn-primary" onClick={openAdd} disabled={!isSupabaseConfigured}>
          + Add Opening
        </button>
      </div>

      <div className="admin-content">
        {!isSupabaseConfigured && (
          <div className="config-warning">
            Add your Supabase keys to <code>.env</code> to enable editing.
          </div>
        )}
        {error && !modalOpen && <div className="admin-alert error">{error}</div>}

        <div className="admin-panel">
          <div className="admin-panel-head">
            <h3>{jobs.length} opening{jobs.length === 1 ? "" : "s"}</h3>
          </div>

          {loading ? (
            <div className="admin-empty">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="admin-empty">No openings yet. Click “Add Opening” to post one.</div>
          ) : (
            <ul className="admin-list">
              {jobs.map((j) => (
                <li className="admin-list-item" key={j.id}>
                  <div className="admin-list-main">
                    <p className="t">
                      {j.title}{" "}
                      <span className={`tag ${j.active !== false ? "on" : "off"}`}>
                        {j.active !== false ? "Live" : "Hidden"}
                      </span>
                    </p>
                    <p className="m">
                      {[j.department, j.location, j.type].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <div className="admin-list-actions">
                    <button className="btn btn-ghost" onClick={() => toggleActive(j)}>
                      {j.active !== false ? "Hide" : "Show"}
                    </button>
                    <button className="btn btn-ghost" onClick={() => openEdit(j)}>Edit</button>
                    <button className="btn btn-danger-text" onClick={() => handleDelete(j)}>Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="admin-modal-backdrop" onMouseDown={() => !saving && setModalOpen(false)}>
          <div className="admin-modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{editingId ? "Edit Opening" : "Add Opening"}</h3>
            {error && <div className="admin-alert error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="admin-field">
                <label>Job title</label>
                <input value={form.title} onChange={setField("title")} required placeholder="Production Manager" />
              </div>

              <div className="admin-row">
                <div className="admin-field">
                  <label>Department</label>
                  <input value={form.department} onChange={setField("department")} placeholder="Manufacturing" />
                </div>
                <div className="admin-field">
                  <label>Location</label>
                  <input value={form.location} onChange={setField("location")} placeholder="Lahore, Pakistan" />
                </div>
              </div>

              <div className="admin-row">
                <div className="admin-field">
                  <label>Type</label>
                  <select value={form.type} onChange={setField("type")}>
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="admin-field">
                  <label>Apply email</label>
                  <input type="email" value={form.applyEmail} onChange={setField("applyEmail")} placeholder="careers@softwoodtextiles.com" />
                </div>
              </div>

              <div className="admin-field">
                <label>Description</label>
                <textarea value={form.description} onChange={setField("description")} placeholder="Role summary, responsibilities, requirements…" />
              </div>

              <div className="admin-field">
                <label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                    style={{ width: "auto", marginRight: "0.5rem" }}
                  />
                  Show on the Careers page
                </label>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Post Opening"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default JobsAdmin;
